import { useCallback, useSyncExternalStore } from 'react'

export interface UseLocalStorageParams {
  key: string
}

export const useLocalStorage = <T = string>({ key }: UseLocalStorageParams) => {
  const getSnapshot = useCallback(() => {
    const item = localStorage.getItem(key)
    if (item) {
      return JSON.parse(item) as T
    }
    return null
  }, [key])

  const setItem = useCallback(
    (value: T) => {
      const newValue = JSON.stringify(value)
      localStorage.setItem(key, newValue)
      dispatchEvent(new StorageEvent('storage', { key, newValue }))
    },
    [key]
  )

  const removeItem = useCallback(() => {
    localStorage.removeItem(key)
    dispatchEvent(new StorageEvent('storage', { key }))
  }, [key])

  const subscribe = (listener: () => void) => {
    window.addEventListener('storage', listener)
    return () => window.removeEventListener('storage', listener)
  }

  const value = useSyncExternalStore(subscribe, getSnapshot)

  return { value, setItem, removeItem }
}
